import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
} from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { s, iconSize, fs, v, borderWidth } from '../../utils/libs';
import { GTFonts, PoppinsFonts, theme } from '../../theme';
import type { LocationObject } from '../../redux/locationSlice';

/**
 * Body and footer of the location sheet, shared by the POS picker
 * (LocationPicker) and the back office header picker so both look and scroll
 * the same. Rows render inside LocationBottomSheet's own scroll view (no
 * nested list), so the whole sheet scrolls.
 */

type FontAwesome6SolidIconName = Extract<
  React.ComponentProps<typeof FontAwesome6>,
  { iconStyle: 'solid' }
>['name'];

type PickerLocation = LocationObject & { bp_location_id?: string };

const getLocationIcon = (locationType?: string): FontAwesome6SolidIconName => {
  const type = locationType?.toLowerCase() || '';
  switch (type) {
    case 'outlet':
    case 'shop':
      return 'store';
    case 'warehouse':
      return 'warehouse';
    case 'office':
      return 'building';
    default:
      return 'location-dot';
  }
};

interface LocationSheetListProps {
  locations: PickerLocation[];
  currentLocationId?: string | null;
  isAllLocations: boolean;
  showAllLocationsOption?: boolean;
  onSelectLocation: (locationId: string) => void;
  onSelectAllLocations: () => void;
}

export const LocationSheetList: React.FC<LocationSheetListProps> = ({
  locations,
  currentLocationId,
  isAllLocations,
  showAllLocationsOption = false,
  onSelectLocation,
  onSelectAllLocations,
}) => (
  <>
    {showAllLocationsOption && (
      <>
        <TouchableOpacity
          style={styles.locationItem}
          onPress={onSelectAllLocations}>
          <View
            style={[
              styles.locationItemContent,
              styles.allLocationsItem,
              isAllLocations && styles.currentLocationItem,
            ]}>
            <FontAwesome6
              name="layer-group"
              iconStyle="solid"
              size={iconSize(20)}
              color={isAllLocations ? theme.colors.successDark : '#6366f1'}
              style={styles.locationIcon}
            />
            <View style={styles.locationInfo}>
              <Text style={styles.locationName}>All Locations</Text>
              <Text style={styles.locationDetails}>
                Tap to view business-wide data
              </Text>
            </View>
            {isAllLocations && (
              <FontAwesome6
                name="circle-check"
                iconStyle="solid"
                size={iconSize(20)}
                color="#22c55e"
                style={styles.currentLocationIcon}
              />
            )}
          </View>
        </TouchableOpacity>
        <View style={styles.divider} />
      </>
    )}

    {locations.map(item => {
      const locationId = item.bp_location_id || item._id;
      // Don't highlight the current location in "All Locations" mode
      const isCurrentLocation =
        !isAllLocations &&
        !!currentLocationId &&
        (item.bp_location_id === currentLocationId ||
          item._id === currentLocationId);

      return (
        <Pressable
          key={item._id ?? locationId}
          onPress={() => locationId && onSelectLocation(locationId)}
          style={({ pressed, hovered }: any) => [
            styles.locationItem,
            Platform.OS === 'web' && styles.locationItemWeb,
            Platform.OS === 'web' && hovered && styles.locationItemHovered,
            pressed && { opacity: 0.85 },
          ]}>
          <View
            style={[
              styles.locationItemContent,
              isCurrentLocation && styles.currentLocationItem,
            ]}>
            <FontAwesome6
              name={getLocationIcon(item.bp_location_type)}
              iconStyle="solid"
              size={iconSize(20)}
              color={isCurrentLocation ? theme.colors.successDark : '#1560bd'}
              style={styles.locationIcon}
            />
            <View style={styles.locationInfo}>
              <Text style={styles.locationName}>{item.bp_location_name}</Text>
              <Text style={styles.locationDetails}>
                {item.bp_location_code} • {item.bp_location_type}
              </Text>
            </View>
            <View style={styles.badgeContainer}>
              {item.bp_location_is_default && (
                <View style={styles.defaultBadge}>
                  <Text style={styles.defaultBadgeText}>Default</Text>
                </View>
              )}
              {isCurrentLocation && (
                <FontAwesome6
                  name="circle-check"
                  iconStyle="solid"
                  size={iconSize(20)}
                  color="#22c55e"
                  style={styles.currentLocationIcon}
                />
              )}
            </View>
          </View>
        </Pressable>
      );
    })}
  </>
);

interface LocationSheetFooterProps {
  onAddBusiness: () => void;
}

export const LocationSheetFooter: React.FC<LocationSheetFooterProps> = ({
  onAddBusiness,
}) => (
  <View style={styles.footerContainer}>
    <TouchableOpacity onPress={onAddBusiness} style={styles.addBusinessButton}>
      <Text style={styles.addBusinessButtonText}>+ Add New Business</Text>
    </TouchableOpacity>
  </View>
);

const styles = StyleSheet.create({
  locationItem: {
    marginBottom: v(16),
  },
  locationItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: s(15),
    borderRadius: s(8),
    borderWidth: borderWidth(2),
    borderColor: '#e9ecef',
  },
  allLocationsItem: {
    borderColor: '#e0e7ff',
    backgroundColor: '#f5f3ff',
  },
  currentLocationItem: {
    backgroundColor: theme.colors.successLight,
    borderColor: theme.colors.successLight,
  },
  locationIcon: {
    marginRight: s(12),
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: '#333',
    marginBottom: v(2),
  },
  locationDetails: {
    fontSize: fs(12),
    lineHeight: fs(16),
    fontFamily: GTFonts.regular,
    color: '#666',
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(5),
  },
  defaultBadge: {
    backgroundColor: '#1560bd',
    paddingHorizontal: s(8),
    paddingVertical: s(4),
    borderRadius: s(12),
  },
  defaultBadgeText: {
    fontSize: fs(10),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: 'white',
  },
  currentLocationIcon: {
    marginLeft: s(5),
  },
  locationItemWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
        } as any)
      : {}),
  },
  locationItemHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: v(8),
  },
  footerContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingVertical: s(20),
    ...(Platform.OS === 'web' && {
      paddingHorizontal: s(24),
    }),
  },
  addBusinessButton: {
    width: '100%',
    backgroundColor: '#1560bd',
    paddingVertical: s(14),
    borderRadius: s(12),
    alignItems: 'center',
  },
  addBusinessButtonText: {
    color: 'white',
    fontFamily: PoppinsFonts.regular,
    fontWeight: '700',
    fontSize: fs(16),
  },
});
